import { ItemPerPageSelector, SearchOrAddData } from '@components/common'
import { UserInfo } from '@components/user'
import { AdasBox, AdasPagination, AdasPaper, AdasStack, AdasTable } from '@components/wrapper-components'
import { Download, Pencil, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getAllUsers, getCalibrationCenter } from '../../../api/api'
import { GLOBAL_DIRECTORY_HEADER, TABLE_HEADER_STYLE } from '../../../constants'
import { scrollPageTop } from '../../../utils/useScrollTop'
import { ccidFormatter, downloadAsFile, processCenterRoles, roundToGreatestInteger } from '../../../utils/utils'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useUserStore } from '@caradasstore/UserStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { Logger } from '../../../logger'

export const ManageUsersListPage = () => {
  const MAX_PAGE_SIZE = 500
  const [searchInput, setSearchInput] = useState('')
  const [users, setUsers] = useState([])
  const [isUserInfo, setIsUserInfo] = useState(false)
  const [userData, setUserData] = useState({})
  const [userCenters, setUserCenters] = useState([])
  const [pageNo, setPageNo] = useState(0)
  const [pageSize, setPageSize] = useState(MAX_PAGE_SIZE)
  const [refreshNow, setRefreshNow] = useState(1)
  const [sort, setSort] = useState('ASC')
  const [sortField, setSortField] = useState('ccid')
  const currentUser = useUserStore((store) => store.currentUser)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const setCenterDetail = useCenterStore((store) => store.setCenterDetail)
  const history = useHistory()

  const paginationHandler = useCallback((_event, page) => {
    setPageNo(page - 1)
    scrollPageTop()
  }, [])

  const pageSizeHandler = useCallback((event) => {
    setPageNo(0)
    setPageSize(event.target.value)
    scrollPageTop()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const ccListResponse = await getAllUsers({
          pageNo: 0,
          pageSize: 2000
        })
        setLoading(false)

        if (ccListResponse?.status === 200) {
          const users = ccListResponse.data.userDisplayDtos.reduce((accum, user) => {
            const [role, centers] = processCenterRoles(user) // getting multiple cc for a single user
            const userData = {
              name: user.name || '',
              email: user.email || '',
              role: role || '',
              is_active: user.is_active || false,
              sourceUser: user,
              centers: centers
            }

            if (centers.length === 0) {
              accum.push({
                ...userData,
                role: role === 'Admin' ? '-- All --' : '-- None --',
                ccid: ccidFormatter(user.calibrationCenterId || ''),
                calibrationCenterId: user.calibrationCenterId
              })
            } else {
              centers.forEach((center) => {
                accum.push({
                  ...userData,
                  center: center.name || '',
                  ccid: ccidFormatter(center.id),
                  calibrationCenterId: center.id
                })
              })
            }

            return accum
          }, [])
          setUsers(users)
          scrollPageTop()
        }
      } catch (error) {
        setLoading(false)

        Logger.error({ message: error, payload: { file: 'ManageUsersListPage', method: 'getAllUsers' } })
      }
    }

    fetchData()
  }, [refreshNow])

  const searchInputHandler = (e) => {
    setSearchInput(e.target.value)
  }

  useEffect(() => {
    setPageNo(0)
  }, [searchInput])

  const userInfoSelect = (content) => {
    setUserCenters(content.centers)
    setUserData(content.sourceUser)
    setIsUserInfo(true)
  }

  const switChCalibrationCenter = async (content) => {
    const ccResponse = await getCalibrationCenter({
      ccid: content.calibrationCenterId
    })
    if (ccResponse?.status === 200) {
      setCenterDetail(ccResponse.data)
      history.push('/')
    }
  }

  const userListSelectionHandler = (content, columnName) => {
    if (columnName === 'edit') {
      userInfoSelect(content)
    } else if (columnName === 'center') {
      switChCalibrationCenter(content)
    }
  }

  const clickSortHandler = useCallback(
    (field) => {
      if (sortField === field) {
        setSort(sort === 'ASC' ? 'DESC' : 'ASC')
      } else {
        setSort('ASC')
        setSortField(field)
      }
    },
    [sort, sortField]
  )

  const filteredSortedUsers = useMemo(() => {
    let filteredList = users
    if (searchInput) {
      filteredList = users.filter((user) => {
        const search = searchInput.toLowerCase()
        return (
          user.name?.toLowerCase().includes(search) ||
          user.email?.toLowerCase().includes(search) ||
          user.ccid?.toLowerCase().includes(search) ||
          user.role?.toLowerCase().includes(search) ||
          user.center?.toLowerCase().includes(search)
        )
      })
    }
    return [...filteredList].sort((a, b) => {
      if (a[sortField] < b[sortField]) {
        return sort === 'ASC' ? -1 : 1
      }
      if (a[sortField] > b[sortField]) {
        return sort === 'ASC' ? 1 : -1
      }
      return 0
    })
  }, [users, searchInput, sort, sortField])

  const userDisplayList = useMemo(() => {
    const startIndex = pageNo * pageSize
    const endIndex = startIndex + pageSize
    return filteredSortedUsers.slice(startIndex, endIndex)
  }, [filteredSortedUsers, pageNo, pageSize])

  const downloadUsers = useCallback((info, activeOnly = false) => {
    const users = info
    let userExport = users.map((user, index) => {
      const calibrationCenterId = user.calibrationCenterId // Extract calibrationCenterId
      const center = user.centers?.find((c) => c.id === calibrationCenterId) // Match center by id
      const groupname = center ? (center.group_name != null ? center.group_name : '') : '' // Get groupname or default to 'N/A'
      return [
        index,
        user.ccid,
        user.center || '',
        user.name,
        user.sourceUser.email,
        user.role,
        groupname,
        user.is_active ? 'Active' : 'Inactive'
      ]
    })
    userExport = [['Index', 'CCID', 'Center', 'Name', 'Email', 'Role', 'Group Name', 'Status'], ...userExport]

    // Convert the data to CSV format (join rows and columns with proper delimiters)
    const csvContent = userExport
      .map((row) => row.map((item) => `"${item}"`).join(',')) // Quote each item and join by commas
      .join('\n') // Join each row by newline
    const fileName = 'manageUserExport.csv'
    const value = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    downloadAsFile(value, fileName)
  }, [])

  const isFilteredExportDisabled = !searchInput && pageSize === MAX_PAGE_SIZE

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-0'>
      {/* Page heading (desktop only) */}
      <AdasBox className='hidden min-[435px]:flex pt-6 md:pt-4 mb-6 flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>Global Directory</h1>
      </AdasBox>

      {/* Mobile persistent search bar */}
      <div className='min-[435px]:hidden mt-3 mb-2 flex items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-3 py-2.5'>
        <Search className='h-4 w-4 text-[#9CA3AF]' />
        <input
          type='text'
          value={searchInput}
          onChange={searchInputHandler}
          placeholder='CC #, CC name, user, role...'
          className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#9CA3AF] focus:outline-none'
        />
      </div>

      {/* Mobile user count */}
      <div className='min-[435px]:hidden mb-3 text-[12px] text-[#6B7280]'>
        {filteredSortedUsers.length} {filteredSortedUsers.length === 1 ? 'user' : 'users'}
      </div>

      {/* Desktop search bar + export */}
      <AdasBox className='hidden min-[435px]:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-5'>
        <AdasBox className='flex items-center flex-1 sm:flex-[0.6]'>
          <SearchOrAddData
            searchInput={searchInput}
            searchInputHandler={searchInputHandler}
            variant='withoutAddButton'
            searchInputPlaceholder='CC #, CC Name, User Name, Role'
          />
        </AdasBox>
        <AdasBox className='flex items-center gap-3 shrink-0'>
          <button
            type='button'
            disabled={isFilteredExportDisabled}
            onClick={() => downloadUsers(userDisplayList)}
            className='inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#D1D5D8] rounded-lg text-[13px] font-semibold text-[#0A1628] hover:bg-[#F8FAFC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <Download className='h-4 w-4' />
            Export Filtered
          </button>
          <button
            type='button'
            onClick={() => downloadUsers(users, true)}
            className='inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#D1D5D8] rounded-lg text-[13px] font-semibold text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
          >
            <Download className='h-4 w-4' />
            Export CSV
          </button>
        </AdasBox>
      </AdasBox>

      {/* Desktop table */}
      <div className='hidden min-[435px]:block bg-card rounded-card border border-border shadow-level-1 overflow-hidden mt-5'>
        {users.length === 0 ? (
          <div className='py-10 text-center text-[13px] text-[#6B7280]'>No users found.</div>
        ) : (
          <AdasTable
            containerProps={{ component: AdasPaper, elevation: 0, className: 'shadow-none' }}
            tableProps={{
              sx: {
                '.MuiTableCell-root': { border: 'none', padding: '2px 10px' },
                overflowX: 'auto'
              },
              'aria-label': 'global table'
            }}
            columns={GLOBAL_DIRECTORY_HEADER}
            sortField={sortField}
            sortDirection={sort}
            onSort={clickSortHandler}
            headerStyle={TABLE_HEADER_STYLE}
            data={userDisplayList}
            onSelect={userListSelectionHandler}
          />
        )}
      </div>

      {/* Mobile card list */}
      <AdasBox className='min-[435px]:hidden space-y-3 pb-6'>
        {userDisplayList.length === 0 ? (
          <div className='rounded-xl border border-[#D1D5D8] bg-white px-4 py-10 text-center text-[14px] text-[#6B7280]'>
            {searchInput ? 'No users match your search.' : 'No users found.'}
          </div>
        ) : (
          userDisplayList.map((user, idx) => {
            const initials =
              (user.name || '')
                .trim()
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0])
                .join('')
                .toUpperCase() || '?'
            const role = user.role || '--'
            return (
              <div
                key={`${user.calibrationCenterId || 'none'}-${user.sourceUser?.id || idx}`}
                onClick={user.center ? () => switChCalibrationCenter(user) : undefined}
                className={`w-full rounded-xl border border-[#D1D5D8] bg-white p-4 text-left transition-colors hover:bg-[#F8FAFC] ${
                  user.center ? 'cursor-pointer' : ''
                }`}
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex min-w-0 flex-1 items-start gap-3'>
                    <div
                      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${
                        user.is_active ? 'bg-[#E5EBF5] text-[#1F6FEB]' : 'bg-[#EBEDF0] text-[#6B7280]'
                      }`}
                    >
                      {initials}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <div className='text-[15px] font-semibold leading-tight text-[#0A1628]'>{user.name || '—'}</div>
                      <div className='mt-0.5 truncate text-[12px] text-[#475569]'>
                        {user.ccid}
                        {user.center ? ` · ${user.center}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className='flex shrink-0 items-center gap-2'>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                        user.is_active ? 'bg-[#DCFCE7] text-[#16803F]' : 'bg-[#EBEDF0] text-[#6B7280]'
                      }`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation()
                        userInfoSelect(user)
                      }}
                      aria-label={`Edit ${user.name || 'user'}`}
                      className='inline-flex h-8 w-8 items-center justify-center rounded-full text-[#475569] transition-colors hover:bg-[#EBEDF0] hover:text-[#1F6FEB]'
                    >
                      <Pencil className='h-4 w-4' />
                    </button>
                  </div>
                </div>
                <div className='mt-3 flex items-center gap-2 border-t border-[#EBEDF0] pt-3 text-[12px]'>
                  <span className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>Role</span>
                  <span className='text-[13px] font-semibold text-[#0A1628]'>{role}</span>
                </div>
              </div>
            )
          })
        )}

        {filteredSortedUsers.length > pageSize && (
          <AdasBox className='flex justify-center pt-2'>
            <AdasPagination
              page={pageNo + 1}
              onChange={paginationHandler}
              count={roundToGreatestInteger(filteredSortedUsers.length / pageSize)}
              shape='rounded'
            />
          </AdasBox>
        )}
      </AdasBox>

      <AdasPaper elevation={0} className='hidden min-[435px]:flex items-center justify-between p-[20px] mt-[20px]'>
        <AdasBox className='w-[20%]'>
          <ItemPerPageSelector pageSizeHandler={pageSizeHandler} pageSize={pageSize} />
        </AdasBox>
        <AdasBox>
          <AdasStack spacing={2}>
            <AdasPagination
              page={pageNo + 1}
              onChange={paginationHandler}
              count={roundToGreatestInteger(filteredSortedUsers.length / pageSize)}
              shape='rounded'
            />
          </AdasStack>
        </AdasBox>
      </AdasPaper>

      {isUserInfo && (
        <UserInfo
          open={isUserInfo}
          allowDelete={false}
          updateClientListHandler={() => setRefreshNow(refreshNow + 1)}
          userInfo={userData}
          closeDrawer={() => setIsUserInfo(false)}
          global={!currentUser.is_admin}
          userCenters={userCenters}
        />
      )}
    </div>
  )
}
